// StarMarker.tsx — A star on the one continuous map. PERFORMANCE: only the
// focused system's star gets the expensive procedural fBm surface + corona; every
// other star (the whole galaxy map) is a cheap unlit sphere, so we never run 200+
// noise shaders at once. Floored to a mode-dependent minimum apparent size; hidden
// when another system is focused. Click flies the camera to its system.
import { useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import { AdditiveBlending, Color, Vector3 } from 'three';
import type { Group, Mesh, PerspectiveCamera } from 'three';
import { useGame } from '../state/context';
import { SPECTRAL } from '../galaxy/spectral';
import type { StarSystem } from '../types';
import { apparentScale, FLOOR_NONE, FLOOR_STAR, FLOOR_STAR_GALAXY } from './apparentScale';
import { Bracket } from './Bracket';
import { glowTexture } from './glow';
import { labelText, LABEL_CANVAS_RATIO } from './labelText';
import {
  STAR_CORONA_FRAG,
  STAR_CORONA_VERT,
  STAR_SURFACE_FRAG,
  STAR_SURFACE_VERT,
} from './starShaders';

interface Props {
  system: StarSystem;
}

const CORONA_SCALE = 1.4;
// Soft additive halo radius as a multiple of the star's core, so every star reads
// as a glowing point (fake bloom) the way a Stellaris star does. Kept modest so the
// halos don't bleed into each other and bury the hyperlanes on the galaxy map.
const STAR_GLOW = 5.5;
// Target on-screen height of the star name text, in CSS px. The label is a GPU sprite
// (not DOM — 220 <Html> labels halved the framerate), rendered with sizeAttenuation
// off so it stays this size at every zoom, matching the 14px DOM entity labels.
const LABEL_PX = 14;

function seedOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0;
  }
  return ((h >>> 0) % 1000) / 10;
}

export function StarMarker({ system }: Props) {
  const { focus, hover, hoveredId, focusedSystemId } = useGame();
  const [local, setLocal] = useState(false);
  const camera = useThree((state) => state.camera);
  const viewportHeight = useThree((state) => state.size.height);
  const visualRef = useRef<Group>(null);
  const coronaRef = useRef<Mesh>(null);
  const info = SPECTRAL[system.spectralClass];
  const hovered = local || hoveredId === system.id;
  const worldPos = useMemo(() => new Vector3(...system.position), [system.position]);
  const wscale = useMemo(() => new Vector3(), []);
  const glow = useMemo(glowTexture, []);
  // A system is "inhabited" if any of its worlds carries population; mark those names
  // with a small house glyph so the galaxy map shows at a glance where people live.
  const inhabited = useMemo(() => system.planets.some((p) => p.pops > 0), [system.planets]);
  const label = useMemo(
    () => labelText(inhabited ? `${system.name} ⌂` : system.name),
    [system.name, inhabited],
  );
  // Constant on-screen size: with sizeAttenuation off a sprite of world-scale s shows
  // a screen height of s · cot(fov/2) · height/2, so invert that to land the TEXT at
  // LABEL_PX px at any zoom (recomputed only on a resize, not per frame).
  const labelScale = useMemo<[number, number, number]>(() => {
    const fov = (camera as PerspectiveCamera).fov ?? 50;
    const sy = (LABEL_PX * LABEL_CANVAS_RATIO * 2 * Math.tan((fov * Math.PI) / 360)) / viewportHeight;
    return [sy * label.aspect, sy, 1];
  }, [camera, viewportHeight, label.aspect]);

  const isFocusedStar = focusedSystemId === system.id;
  const visible = focusedSystemId === null || isFocusedStar;
  // Stars are only hover/click targets on the open galaxy map. Once a system is
  // focused, its star is just distant scenery — and its far, apparent-size-floored
  // hit sphere otherwise catches "background" hovers and pops a label behind the
  // planet you're looking at.
  const interactive = focusedSystemId === null;
  const floor =
    focusedSystemId === null ? FLOOR_STAR_GALAXY : isFocusedStar ? FLOOR_STAR : FLOOR_NONE;

  const surfaceUniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uSeed: { value: seedOf(system.id) },
      uHot: { value: new Color(info.core) },
      uCool: { value: new Color(info.light).multiplyScalar(0.55) },
    }),
    [system.id, info],
  );
  const coronaUniforms = useMemo(
    () => ({
      uCameraPos: { value: new Vector3() },
      uCenter: { value: worldPos.clone() },
      uStarRadius: { value: system.radius },
      uCoronaRadius: { value: system.radius * CORONA_SCALE },
      uColor: { value: new Color(info.light) },
      uIntensity: { value: 1.0 },
      uFalloff: { value: 3.0 },
    }),
    [info, system.radius, worldPos],
  );

  useFrame((state) => {
    if (!visible) {
      return;
    }
    if (visualRef.current) {
      visualRef.current.scale.setScalar(apparentScale(camera, worldPos, system.radius, floor));
    }
    if (isFocusedStar) {
      surfaceUniforms.uTime.value = state.clock.elapsedTime;
      const c = coronaRef.current;
      if (c) {
        c.getWorldScale(wscale);
        const coronaR = system.radius * wscale.x;
        coronaUniforms.uCameraPos.value.copy(camera.position);
        coronaUniforms.uCenter.value.copy(worldPos);
        coronaUniforms.uCoronaRadius.value = coronaR;
        coronaUniforms.uStarRadius.value = coronaR / CORONA_SCALE;
      }
    }
  });

  const onOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setLocal(true);
    hover(system.id);
    document.body.style.cursor = 'pointer';
  };
  const onOut = () => {
    setLocal(false);
    hover(null);
    document.body.style.cursor = 'auto';
  };
  const onClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    focus(system.id);
  };

  return (
    <group position={system.position} visible={visible}>
      <group ref={visualRef}>
        <sprite scale={[system.radius * STAR_GLOW, system.radius * STAR_GLOW, 1]} renderOrder={1}>
          <spriteMaterial
            map={glow}
            color={info.light}
            transparent
            opacity={0.9}
            depthWrite={false}
            blending={AdditiveBlending}
            toneMapped={false}
          />
        </sprite>
        {isFocusedStar ? (
          <>
            <mesh
              onPointerOver={interactive ? onOver : undefined}
              onPointerOut={interactive ? onOut : undefined}
              onClick={interactive ? onClick : undefined}
            >
              <sphereGeometry args={[system.radius, 32, 24]} />
              <shaderMaterial
                vertexShader={STAR_SURFACE_VERT}
                fragmentShader={STAR_SURFACE_FRAG}
                uniforms={surfaceUniforms}
                toneMapped={false}
              />
            </mesh>
            <mesh ref={coronaRef} scale={CORONA_SCALE} renderOrder={2}>
              <sphereGeometry args={[system.radius, 24, 16]} />
              <shaderMaterial
                vertexShader={STAR_CORONA_VERT}
                fragmentShader={STAR_CORONA_FRAG}
                uniforms={coronaUniforms}
                transparent
                depthWrite={false}
                blending={AdditiveBlending}
                toneMapped={false}
              />
            </mesh>
          </>
        ) : (
          <mesh
            onPointerOver={interactive ? onOver : undefined}
            onPointerOut={interactive ? onOut : undefined}
            onClick={interactive ? onClick : undefined}
          >
            <sphereGeometry args={[system.radius, 16, 12]} />
            <meshBasicMaterial color={info.core} toneMapped={false} />
          </mesh>
        )}
      </group>
      {hovered && interactive ? <Bracket /> : null}
      {interactive ? (
        <sprite
          position={[0, 0, 0]}
          center-x={0.5}
          center-y={-0.55}
          scale={labelScale}
          renderOrder={4}
        >
          <spriteMaterial
            map={label.texture}
            transparent
            opacity={hovered ? 1 : 0.62}
            depthWrite={false}
            depthTest={false}
            toneMapped={false}
            sizeAttenuation={false}
          />
        </sprite>
      ) : null}
    </group>
  );
}
